import { TEAM_PERMISSION_EVERYONE } from "@/components/TeamPermissioningFormSection/TeamPermissioningFormSection"
import { PLACEHOLDERS_NOT_VALID_ERROR } from "@/pages/campaign-form/constants"
import { createSafedSearchObject, validatePlaceholdersForCampaign } from "@/services/campaigns"
import { UseFormReturn } from "react-hook-form"
import type { AllowedCampaignType, CampaignFormValues } from "@/pages/campaign-form/types"
import type { Placeholder } from "@/types/djangio"
import { LINK_SHARE_LINK_TYPE } from "@/pages/campaign-form/components/campaigns/shareALink/Message"
import { isInternational } from "shared/imports/regionConstants"
import { PERSONALIZED_PAGES_KEY } from "@/pages/campaign-form/components/campaigns/personalizedMessages/Messaging"
import { Props } from "@/pages/campaign-form/validators"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

const { ActionCenterBackgroundStyle, CallToActionMediaType } = DjangIO.app.grassroots.enums
const { GamificationPointsMode } = DjangIO.app.grassroots.types
const { DraftOptions, CampaignType } = DjangIO.app.grassroots.campaign.types
const {
    CampaignLayoutType,
    CampaignPageLayout,
    CampaignDescriptionAlignment,
    CampaignNavigationBarType,
    CampaignNameDisplayOptionType,
    CampaignStatus,
    CommentCampaignOrganizationTypes,
} = DjangIO.app.grassroots.campaign.types

export const validatePlaceholders = async (
    message: string | undefined,
    formValues?: Record<string, any>,
): Promise<boolean | string> => {
    const messageHasPlaceholders = message?.includes("{{")
    if (!messageHasPlaceholders) return true

    const customQuestionsIds = formValues?.[PERSONALIZED_PAGES_KEY]?.[0]?.map((question) => question.id) ?? []
    const response = await validatePlaceholdersForCampaign(message, customQuestionsIds)
    if (response?.errors?.length) {
        return `${PLACEHOLDERS_NOT_VALID_ERROR} ${response?.errors?.join(", ")}`
    }
}

export const getDefaultMessageDummy = () => ({
    name: "Message 1",
    archived: false,
    uses_default_member_list: !isInternational,
    custom_targets: [],
    advocate_editing_permission: DjangIO.app.grassroots.campaign.types.DraftOptions.can_edit.value,
    message_variations_type: DjangIO.app.grassroots.campaign.types.MessageVariationsType.none.value,
    message_variations: [
        {
            subject: "",
            fixed_pre_content: "",
            content: "",
            fixed_post_content: "",
            archived: false,
            index: 0,
            ai_generation_type: null,
        },
    ],
    has_custom_email_targets: false,
    index: 0,
})

export const getCampaignFormDefaultValues: (
    searchparams?: URLSearchParams,
    extraParams?: Record<string, any>,
) => Partial<CampaignFormValues> = (searchParams: URLSearchParams = new URLSearchParams(), extraParams = {}) => {
    //If the user goes straight to /campaigns/new, we make it a write a letter campaign
    const campaignType = CampaignType[searchParams?.get("campaign_type")]?.value ?? null

    const multiActionChildrenTypes = searchParams?.get("multi_action_children")
        ? searchParams?.get("multi_action_children")?.split(",").map(Number)
        : []

    const multiActionOrderedChildrenIds = multiActionChildrenTypes?.map((type: number) => type)

    const multiActionChildren = multiActionChildrenTypes?.map((type: number) => ({
        ...getCampaignFormDefaultValues(new URLSearchParams(), extraParams),
        id: null,
        campaign_type: type as AllowedCampaignType,
    })) as Partial<CampaignFormValues[]>

    const firstChildCampaignKey = `${multiActionChildrenTypes?.[0]}`

    return {
        // LAYOUT
        ...extraParams,
        campaign_layout: CampaignLayoutType.standard.value,
        page_layout_type: CampaignPageLayout.two_columns.value,
        description_alignment_type: CampaignDescriptionAlignment.top_left.value,
        full_bleed_header_type: CallToActionMediaType.none.value.toString(),
        background_style_type: ActionCenterBackgroundStyle.org_design.value.toString(),
        background_color_override: "#ffffff",
        navigation_bar_type: CampaignNavigationBarType.standard.value.toString(),
        summary_display_option_type: CampaignNameDisplayOptionType.include_in_main_text.value.toString(),
        show_num_actions: true,
        should_use_footer_html: false,

        // BASICS
        // TODO registration_page - requires complex changes to the useActionCenterSettings query
        multiple_actions_allowed: true,
        is_one_click_registration_enabled: true,
        gamification_points_mode: GamificationPointsMode.default.value.toString(),
        custom_gamification_points: 10,
        participants_saved_searches: [],
        teamPermissioning: TEAM_PERMISSION_EVERYONE,

        // POST NAVIGATION
        action_post_navigation: "thank_you_message",
        thank_you_message: `Thank you for supporting this campaign!`,
        social_media_platforms: [DjangIO.app.grassroots.enums.SocialMediaType.facebook.value],
        should_send_confirmation_email: true,
        confirmation_email_from_address_username: Userdata.email?.split("@")?.[0],
        confirmation_email_from_address_domain: Userdata.default_domain,
        conf_email_from_name: Userdata.name,
        conf_email_reply_to_name: Userdata.name,
        conf_email_reply_to_address: Userdata.email,

        //FINALIZE
        visible_on_action_center: true,

        // Other
        campaign_type: campaignType,
        status: CampaignStatus.draft.value,
        order: "2",

        //WRITE A LETTER CAMPAIGN TYPE
        // BASICS
        require_confirmation: false,
        supporters_can_choose_targets: false,
        targeting_type: DjangIO.app.grassroots.campaign.types.CampaignTargetingType.advocate_address.value,

        // MESSAGE
        uses_default_member_list: !isInternational,
        //standard_options_search => see createOfficialsSafedSearch method
        primary_targets_saved_searches: [],
        message_groups: [getDefaultMessageDummy()],

        // CALL A NUMBER
        multi_messages: [""],

        // POST AT SOMEONE
        targets_district_office: false,

        // COMMENT ON REGULATION
        submitting_organization_name: null,
        comment_organization_type: CommentCampaignOrganizationTypes.default.value,

        // SHARE A LINK
        share_link: LINK_SHARE_LINK_TYPE, // Once campaigns are added to Share Link on the backend this should default to "link" if there are no campaigns

        // LOG RELATIONSHIP
        relationship_types: [],

        // REQUEST A VISIT
        draft_requirements: DraftOptions.can_edit.value,

        // SHARE A STORY
        share_story_type: DjangIO.app.grassroots.campaign.types.ShareStoryType.text.value,
        share_story_video_duration: 60,
        video_notified_users: [],

        // MULTI-ACTION
        multi_action_children: multiActionChildren ?? [],
        multi_action_parent_id: null,
        multi_action_ordered_children_ids: multiActionOrderedChildrenIds ?? [],

        policy_areas: [],

        // UI
        child_selected_for_actions: firstChildCampaignKey,
        child_selected_for_gamification: firstChildCampaignKey,
        ui_form_active_action: firstChildCampaignKey,
        ui_custom_targets_enabled: false,

        // PERSONALIZED MESSAGES
        has_custom_email_targets: false,
        custom_email_targets: [],
        personalized_campaign_pages: {},

        // AI
        should_rework_with_ai: false,
        ai_options: {
            length: "short",
            tone: "friendly",
        },
    }
}

const hasGroups = [
    DjangIO.app.grassroots.campaign.types.CampaignType.write_member.value,
    DjangIO.app.grassroots.campaign.types.CampaignType.personalized_messages.value,
]

export const createOfficialsSafedSearch = async (
    formContext: UseFormReturn<CampaignFormValues> & object,
    messageGroupIndex = 0,
    prefix?: string,
) => {
    const campaignType = formContext.getValues(`${prefix ? prefix + "." : ""}campaign_type`)
    const safedSearchFieldName = hasGroups.includes(campaignType)
        ? `${prefix ? prefix + "." : ""}message_groups[${messageGroupIndex}].standard_options_search`
        : `${prefix ? prefix + "." : ""}default_targets_saved_search`
    const currentTargets = formContext.getValues(safedSearchFieldName)
    if (currentTargets) return
    const searchifyFilters = { querysetList: [{ ph_minor_role_type: [5, 6, 7, 8] }] }
    const savedSearch = await createSafedSearchObject(
        { searchifyFilters },
        DjangIO.app.models.QuorumDataType.person,
        "Default Lists",
    )

    formContext.setValue(safedSearchFieldName, savedSearch)
}

export const isEmpty = (value) => {
    if (!value || value === "" || value === "undefined" || value === "null" || value?.length === 0) {
        return true
    }
    return false
}

export const filterContactPlaceholders = (placeholders: Placeholder[]) => {
    // Logic took from shared/campaign/campaign-selectors.js::selectContactPlaceholders
    const { grassroots_contact } = DjangIO.app.userdata.outbox.types.PlaceholderType
    const contactsCategory = placeholders?.filter(
        (placeholderCategory) =>
            placeholderCategory &&
            // if it is a number, compare equality, if it is a grouped enum,
            // check that it is contained in the valid_for field
            (typeof placeholderCategory.valid_for === "number"
                ? placeholderCategory.valid_for === grassroots_contact.value
                : placeholderCategory.valid_for.includes(grassroots_contact.value)),
    )

    return contactsCategory?.reduce((acc, placeholder) => {
        return [...acc, ...placeholder.items]
    }, [])
}

export const hasAnyOfTheseCampaignTypesInMultiaction = (campaignTypes: number[], reactHookForm) => {
    const { getValues } = reactHookForm
    const typesIn = getValues("multi_action_children") ?? []
    return typesIn.some((type) => campaignTypes.includes(type.campaign_type))
}

export const hasAllOfTheseCampaignTypesInMultiaction = (campaignTypes: number[], reactHookForm) => {
    const { getValues } = reactHookForm
    const typesIn = getValues("multi_action_children") ?? []
    return typesIn.every((type) => campaignTypes.includes(type.campaign_type))
}

/**
 * getNamePrefixCampaignTypeChild provides a prefix in the form:  'multi_action_children[n]' where n
 * is the multi-action child position in the array 'multi-action-children' according to the parameter
 * campaignTypeValue.
 *
 * Adding this prefix to any form name creates a multi-action children context for form controllers.
 *
 * It also provides an isolation that prevents eventual multi-action children properties conflicts due overlapping.
 */
export const getNamePrefixForCampaignTypeChild = (campaignTypeValue: number, formContext) => {
    const multiActionChildrenTypes = formContext.getValues("multi_action_children")
    if (!multiActionChildrenTypes || multiActionChildrenTypes.length === 0) return null
    const multiActionChildrenIndex = (campaignTypeValue: number) => {
        return multiActionChildrenTypes.findIndex((value) => value.campaign_type === campaignTypeValue)
    }
    return getMultiActionPrefixByIndex(multiActionChildrenIndex(campaignTypeValue))
}

/**
 * getNameFieldForMAChild provides a name field with a prefix for multi-action children.
 * if the parent campaign is not a multi-action campaign, it returns the fieldName as it is.
 */
export const getNameFieldForMAChild = (formContext, campaignTypeMAChild: number, fieldName: string) => {
    if (
        formContext.getValues("campaign_type") !== DjangIO.app.grassroots.campaign.types.CampaignType.multi_action.value
    )
        return fieldName
    const namePrefix = getNamePrefixForCampaignTypeChild(campaignTypeMAChild, formContext)
    return namePrefix ? `${namePrefix}.${fieldName}` : fieldName
}

export const getMultiActionPrefixByIndex = (index: number) => `multi_action_children[${index}]`

export const validateHasQuestionAttached = (props: Props) => {
    const pages = props.watch(PERSONALIZED_PAGES_KEY)
    const hasQuestionAttached = Object.values(pages)[0]?.length

    if (hasQuestionAttached) {
        props.clearErrors(PERSONALIZED_PAGES_KEY)
        return true
    }

    props.setError(PERSONALIZED_PAGES_KEY, { type: "custom", message: "Please attach at least one question" })
}

export const isCustomTargetingEnabled = (formContext) => {
    if (!isFeatureEnabled("ff_custom_official_v2")) {
        return false
    }
    return !formContext.getValues("id") || formContext.getValues("is_custom_targeting_enabled")
}
